<template>
    <div class="chat-widget" v-animate-css="{ classes: 'slideInUp', delay: 0, duration: 300 }">
        <section class="header">
            <span class="material-icons text-info" style="font-size: 50px"> chat </span>
            <div class="d-flex flex-column">
                <strong class="font-weight-bold" style="font-size: 25px; letter-spacing: 1.5px">Chat Bot</strong>
                <small class="text-success">
                    <a-badge color="green" />
                    Online
                </small>
            </div>
            <div>
                <md-button class="md-icon-button">
                    <md-icon>more_vert</md-icon>
                </md-button>
                <md-button class="md-icon-button" @click="closeWidget">
                    <md-icon>close</md-icon>
                </md-button>
            </div>
        </section>
        <section class="chat-conversation">
            <div class="conversation">
                <MessageList
                    :should-scroll="false"
                    :always-scroll-to-bottom="true"
                    v-if="getMessages().length"
                    :colors="colors"
                    :messages="getMessages()"
                    :participants="participants"
                    :scroll-top="true"
                    :is-private-chat="true"
                />
            </div>
        </section>
        <section class="footer">
            <UserInput
                id="privateChatUserInput"
                :colors="colors"
                :onSubmit="sendMessage"
                :placeholder="$t('message.typeHere')"
                :showEmoji="true"
                :showFile="false"
                :disabled="closed || !$_.get(this.sezar, 'userInputAllowed', true)"
                :new-icon="true"
            />
        </section>
        <InFrame
            v-if="openMoment.visible"
            @close="openMoment.visible = false"
            :url="openMoment.url"
            :size="openMoment.size"
        />
    </div>
</template>

<script>
import { sendSocketMessage, socketMessageStream } from "../../../../../../utils/socketStream";
import { generateUUID } from "../../../../../../utils/session";
import _ from "lodash";
import log2 from "../../../../../../utils/log2";
import { filter } from "rxjs/operators";
import { mapMutations, mapState } from "vuex";
import MessageList from "../../../../../../components/popup-chat/MessageList";
import { toNewMessagesModel } from "../../../../../../utils/chat";
import UserInput from "../../../../../../components/popup-chat/UserInput";
import InFrame from "./InFrame";
import SYSTEM_PUBSUB from "../../../../../../utils/systemPubSub";

const log = log2("ChatWidget");
export default {
    name: "ChatWidget",
    props: ["diagram", "storyId", "version", "testStartBlockId"],
    components: { InFrame, MessageList, UserInput },
    data() {
        return {
            currentBlockId: this.testStartBlockId,
            userInputAllowed: true,
            messages: [],
            conversationId: null,
            participants: [
                {
                    id: "chatbot",
                    name: "Bot",
                },
                {
                    id: "you",
                    name: "You",
                },
            ],
            typingMessage: false,
            sezar: null,
            colors: {
                header: {
                    bg: "#4e8cff",
                    text: "#ffffff",
                },
                launcher: {
                    bg: "#4e8cff",
                },
                messageList: {
                    bg: "#ffffff",
                },
                sentMessage: {
                    bg: "#4e8cff",
                    text: "#ffffff",
                },
                receivedMessage: {
                    bg: "#eaeaea",
                    text: "#222222",
                },
                userInput: {
                    bg: "#f4f7f9",
                    text: "#565867",
                },
            },
            openMoment: {
                url: null,
                visible: false,
                size: "full",
            },
            closed: false,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => _.get(state.site.site, "_id"),
        }),
    },
    mounted() {
        log("mounted");
        this.subscribeSocketEvents();
        this.startConversation();
    },
    methods: {
        ...mapMutations({
            setConversationId: "CB/SET_CONVERSATION",
        }),
        getUser(userId) {
            return this.getParticipants().find((x) => x.id === userId);
        },
        getMessages() {
            let allMessages = toNewMessagesModel(this.messages, this, true);
            if (this.typingMessage) {
                allMessages = [
                    ...allMessages,
                    {
                        type: "typing",
                        data: {
                            text: this.typingMessage,
                        },
                    },
                ];
            }
            log("allMessages", { allMessages, messages: this.messages });
            return allMessages;
        },
        sendMessage(message) {
            // if (!_.get(this.sezar, "userInputAllowed", false)) return;
            sendSocketMessage("chatbot/v2 test-user-message", {
                siteId: this.siteId,
                conversationId: this.conversationId,
                message,
            });
            const messageModel = {
                messageType: "visitor",
                siteId: this.siteId,
                conversation: this.conversationId,
                description: message,
                baseLang: "tr",
                translates: [],
                seen: true,
                createdDate: Date.now(),
                uniqMessageId: generateUUID(),
                systemMessageType: null,
                visitor: null,
                user: null,
                asset: null,
                location: null,
                createdBy: null,
                updatedBy: null,
            };
            this.messages.push(messageModel);
        },
        startConversation() {
            this.messages = [];
            this.conversationId = "test-" + generateUUID();
            this.setConversationId(this.conversationId);
            log("startConversation", this.conversationId);
            sendSocketMessage("chatbot/v2 test-start-conversation", {
                siteId: this.siteId,
                startBlockId: this.testStartBlockId,
                conversationId: this.conversationId,
                storyId: this.storyId,
                version: this.version,
            });
        },
        closeConversation() {
            sendSocketMessage("chatbot/v2 test-close-conversation", {
                siteId: this.siteId,
                conversationId: this.conversationId,
            });
        },
        resetConversation() {
            if (this.conversationId) {
                this.closeConversation(this.conversationId);
            }
            this.messages = [];
            this.startConversation();
        },
        subscribeSocketEvents() {
            this.socketEventSubscriber = socketMessageStream
                .pipe(filter((x) => x.cmd.startsWith("chatbot/v2")))
                .subscribe(({ cmd, data }) => {
                    log("chatbot v2 event", cmd, data);
                    if (cmd === "chatbot/v2 typing_indicator") {
                        this.typingMessage = true;
                    } else if (cmd === "chatbot/v2 state_changed") {
                        this.sezar = data;
                    } else if (cmd === "chatbot/v2 message") {
                        const { payload, type } = data;
                        switch (type) {
                            case "text":
                                this.handleMessage(payload);
                                break;
                            case "button":
                                this.handleButton(payload);
                                break;
                            case "gallery":
                                this.handleGallery(payload);
                                break;
                            case "image":
                                this.handleImage(payload);
                                break;
                            case "file":
                                this.handleFile(payload);
                                break;
                            case "get-location":
                                this.handleGetLocation(payload);
                                break;
                            case "visitor-message":
                                this.handleVisitorMessage(payload);
                                break;
                        }
                    } else if (cmd === "chatbot/v2 chat_closed") {
                        this.handleClose(data.conversationId);
                    } else if (cmd === "chatbot/v2 action close") {
                        this.openMoment.visible = false;
                    } else if (cmd === "chatbot/v2 action") {
                        const { payload, type } = data;
                        switch (type) {
                            case "OPEN_URL":
                                window.open(payload.url, "_blank");
                                break;

                            case "PHONE_CALL":
                                window.open("tel:" + payload.phoneNumber, "_self");
                                break;

                            case "OPEN_MOMENT":
                                this.openMoment.url = payload.url;
                                this.openMoment.visible = true;
                                this.openMoment.size = payload.windowSize;
                                break;

                            case "RESERVATION_OPEN_MOMENT":
                                this.openMoment.url = payload.url;
                                this.openMoment.visible = true;
                                this.openMoment.size = payload.windowSize;
                                break;
                        }
                    }
                });
        },
        handleMessage(text) {
            const messageModel = {
                messageType: "chatbot",
                siteId: this.siteId,
                conversation: this.conversationId,
                description: text,
                baseLang: "tr",
                translates: [],
                seen: true,
                createdDate: Date.now(),
                uniqMessageId: generateUUID(),
                systemMessageType: null,
                visitor: null,
                user: null,
                asset: null,
                location: null,
                createdBy: null,
                updatedBy: null,
            };
            this.messages.push(messageModel);
            this.typingMessage = false;
        },
        handleGetLocation(payload) {
            const messageModel = {
                messageType: "chatbot-get-location",
                type: "chatbot-get-location",
                siteId: this.siteId,
                conversation: this.conversationId,
                description: "",
                baseLang: "tr",
                translates: [],
                seen: true,
                createdDate: Date.now(),
                uniqMessageId: generateUUID(),
                systemMessageType: null,
                visitor: null,
                user: null,
                asset: null,
                location: null,
                createdBy: null,
                updatedBy: null,
                payload,
            };
            this.messages.push(messageModel);
            this.typingMessage = false;
        },
        handleClose(conversationId) {
            log("handleClose", conversationId);
            const messageModel = {
                messageType: "system",
                siteId: this.siteId,
                conversation: conversationId,
                description: "Chat closed by bot",
                baseLang: "tr",
                translates: [],
                seen: true,
                createdDate: Date.now(),
                uniqMessageId: generateUUID(),
                systemMessageType: "chatbot-destroy",
                visitor: null,
                user: null,
                asset: null,
                location: null,
                createdBy: null,
                updatedBy: null,
            };
            this.messages.push(messageModel);
            this.typingMessage = false;
            this.closed = true;
        },
        handleImage(imageUrl) {
            const messageModel = {
                messageType: "chatbot-image",
                type: "chatbot-image",
                siteId: this.siteId,
                conversation: this.conversationId,
                payload: { imageUrl },
                baseLang: "tr",
                translates: [],
                seen: true,
                createdDate: Date.now(),
                uniqMessageId: generateUUID(),
                systemMessageType: null,
                visitor: null,
                user: null,
                asset: null,
                location: null,
                createdBy: null,
                updatedBy: null,
            };
            this.messages.push(messageModel);
            this.typingMessage = false;
        },
        handleFile(fileUrl) {
            const messageModel = {
                messageType: "chatbot-file",
                type: "chatbot-file",
                siteId: this.siteId,
                conversation: this.conversationId,
                payload: { fileUrl },
                baseLang: "tr",
                translates: [],
                seen: true,
                createdDate: Date.now(),
                uniqMessageId: generateUUID(),
                systemMessageType: null,
                visitor: null,
                user: null,
                asset: null,
                location: null,
                createdBy: null,
                updatedBy: null,
            };
            this.messages.push(messageModel);
            this.typingMessage = false;
        },
        handleGallery({ id, cards, blockId }) {
            const messageModel = {
                messageType: "chatbot-gallery",
                type: "chatbot-gallery",
                siteId: this.siteId,
                conversation: this.conversationId,
                payload: { id, cards, blockId },
                baseLang: "tr",
                translates: [],
                seen: true,
                createdDate: Date.now(),
                uniqMessageId: generateUUID(),
                systemMessageType: null,
                visitor: null,
                user: null,
                asset: null,
                location: null,
                createdBy: null,
                updatedBy: null,
            };
            this.messages.push(messageModel);
            this.typingMessage = false;
        },
        handleButton({ id, buttons, message, type, blockId }) {
            if (type === "BUTTON") {
                const messageModel = {
                    messageType: "chatbot-button",
                    type: "chatbot-button",
                    siteId: this.siteId,
                    conversation: this.conversationId,
                    payload: { id, buttons, message, blockId },
                    baseLang: "tr",
                    translates: [],
                    seen: true,
                    createdDate: Date.now(),
                    uniqMessageId: generateUUID(),
                    systemMessageType: null,
                    visitor: null,
                    user: null,
                    asset: null,
                    location: null,
                    createdBy: null,
                    updatedBy: null,
                };
                this.messages.push(messageModel);
            } else if (type === "QUICK_REPLY") {
                const messageModel = {
                    messageType: "chatbot-quick-reply",
                    type: "chatbot-quick-reply",
                    siteId: this.siteId,
                    conversation: this.conversationId,
                    payload: { id, buttons, message, blockId },
                    baseLang: "tr",
                    translates: [],
                    seen: true,
                    createdDate: Date.now(),
                    uniqMessageId: generateUUID(),
                    systemMessageType: null,
                    visitor: null,
                    user: null,
                    asset: null,
                    location: null,
                    createdBy: null,
                    updatedBy: null,
                };
                this.messages.push(messageModel);
            }
            this.typingMessage = false;
        },
        handleVisitorMessage(text) {
            const messageModel = {
                messageType: "visitor",
                siteId: this.siteId,
                conversation: this.conversationId,
                description: text,
                baseLang: "tr",
                translates: [],
                seen: true,
                createdDate: Date.now(),
                uniqMessageId: generateUUID(),
                systemMessageType: null,
                visitor: null,
                user: null,
                asset: null,
                location: null,
                createdBy: null,
                updatedBy: null,
            };
            this.messages.push(messageModel);
        },
        closeWidget() {
            if (this.conversationId) this.closeConversation(this.conversationId);
            this.$emit("close-menu", 0);
        },
        sendButtonMessage(type, payload) {
            sendSocketMessage("chatbot/v2 test-user-button-select", {
                siteId: this.siteId,
                conversationId: this.conversationId,
                type,
                payload,
            });
        },
    },
    beforeDestroy() {
        this.socketEventSubscriber.unsubscribe();
        SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_CHAT_WIDGET_OPEN_STATE_CHANGED, {
            visible: false,
        });
    },
    watch: {
        testStartBlockId(newBlockId) {
            this.resetConversation();
        },
        diagram: {
            handler(newDiagram) {
                this.resetConversation();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.chat-widget {
    border-radius: 15px;
    box-shadow: 0 0 15px rgb(0 0 0 / 10%);
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    height: calc(100vh - 150px);
    max-height: 100%;
    max-width: 100%;
    overflow: hidden;
    pointer-events: all;
    position: relative;
    user-select: text;
    width: 375px;
    z-index: 999;

    .header {
        align-items: center;
        border-radius: 15px 15px 0 0;
        display: flex;
        height: 100px;
        padding: 0 24px;
        justify-content: space-between;
        background: white;
    }

    .chat-conversation {
        flex-grow: 1;
        height: 0;
        position: relative;
        background: rgb(234, 238, 243);

        .conversation {
            display: flex;
            flex: 1 0 0;
            flex-direction: column;
            height: 100%;
            overflow-x: hidden;
            overflow-y: auto;
            overscroll-behavior-y: contain;
            width: 100%;
        }
    }

    section.footer {
        border-top-color: rgb(234, 234, 234);
        background: rgb(255, 255, 255);
        align-items: center;
        border-radius: 0 0 15px 15px;
        display: flex;
        width: 100%;
        height: 60px;

        .sc-user-input-wrapper {
            .sendIcon {
                color: rgb(18, 91, 251);
                cursor: pointer;
            }

            .btnEmoji {
                svg {
                    stroke: rgb(18, 91, 251) !important;
                }
            }

            &.disabled {
                .sendIcon {
                    color: #c4c7c9;
                    cursor: not-allowed;
                }

                .btnEmoji {
                    cursor: not-allowed;

                    svg {
                        stroke: #c4c7c9 !important;
                        cursor: not-allowed;
                    }
                }
            }
        }
    }

    .sc-message-list {
        background-color: transparent !important;
    }
}
</style>
